import API from "../constants/api";
import axiosInstance, { axiosException } from "./axios";

/**
 * Makes a login request to the API
 * @param {string} email - email to login with
 * @param {string} password - password to login with
 * @returns {Promise<object>} - object containing success, data and message
 */
export const loginRequest = async (email, password) => {
  try {
    const response = await axiosInstance.post(API.LOG_IN, { email, password });
    return {
      success: true,
      data: response.data,
      message: "success",
    };
  } catch (e) {
    const result = axiosException(e);
    return {
      ...result,
      code: e.response?.data?.code ?? null,
      userId: e.response?.data?.user_id ?? null,
    };
  }
};

/**
 * Makes a registration request to the API
 * @param {object} payload - object containing all the registration information
 * @returns {Promise<object>} - object containing success, data and message
 */
export const registerRequest = async (payload) => {try {
    const response = await axiosInstance.post(API.REGISTER, payload);
    return {
      success: true,
      data: response.data,
      message: "success",
    };
  } catch (e) {
    return axiosException(e);
  }
};

/**
 * Makes a forgot password request to the API
 * @param {string} email - email to send password reset instructions to
 * @returns {Promise<object>} - object containing success, data and message
 */
export const forgotPasswordRequest = async (email) => {
  try {
    const response = await axiosInstance.post(API.FORGOT_PASSWORD, {
      email,
    });
    return response.data;
  } catch (e) {
    return axiosException(e);
  }
};



/**
 * Makes a password reset request to the API
 * @param {string} email - email of the user who wants to reset their password
 * @param {string} reset_code - reset code provided by the forgot password request
 * @param {string} new_password - new password to set
 * @param {string} confirm_password - confirmation of the new password
 * @returns {Promise<object>} - object containing success, data and message
 */
export const resetPasswordRequest = async (payload) => {
  try {
    const response = await axiosInstance.post(API.RESET_PASSWORD, payload);
    return response.data;
  } catch (e) {
    return axiosException(e);
  }
};

/**
 * Makes a get profile request to the API
 * @returns {Promise<object>} - object containing success, data and message
 */
export const getProfileRequest = async () => {
  try {
    const response = await axiosInstance.get(API.PROFILE);
    return response.data.data;
  } catch (e) {
    return axiosException(e);
  }
};

/**
 * Makes an update profile request to the API
 * @param {FormData} formData - FormData object containing the updated profile information
 * @returns {Promise<object>} - object containing success, data and message
 */
export const updateProfileRequest = async (formData) => {
  try {
    const response = await axiosInstance.post(API.PROFILE, formData, {headers: {'Content-Type': 'multipart/form-data'}});
    return response.data;
  } catch (e) {
    return axiosException(e);
  }
};

/**
 * Sends an OTP to the specified phone number to verify the phone number
 * 
 * @param {number} user_id - the user ID
 * @param {string} phone_number - the phone number to send the OTP to
 * @returns {Promise<object>} - object containing success, data and message
 */
export const sendWhatsAppOtp = async (user_id, phone_number) => {
  try {
    const response = await axiosInstance.post(API.WHATSAPP_SEND_OTP, { user_id, phone_number });
    return { success: true, data: response.data };
  } catch (e) {
    return axiosException(e);
  }
};

/**
 * Verifies a WhatsApp OTP for the specified user ID
 * @param {number} user_id - the user ID
 * @param {string} otp - the OTP to verify
 * @returns {Promise<object>} - object containing success, data and message
 */
export const verifyWhatsAppOtp = async (user_id, otp) => {
  try {
    const response = await axiosInstance.post(API.WHATSAPP_VERIFY_OTP, { user_id, token: otp });
    return { success: true, data: response.data };
  } catch (e) {
    return axiosException(e);
  }
};

/**
 * Verifies the email OTP for a registered user
 * @param {number} user_id - the user ID returned from registration
 * @param {string} otp - the OTP code sent to the user's email
 * @returns {Promise<object>} - object containing success, data and message
 */
export const verifyEmailByLink = async (user_id, token) => {
  try {
    const response = await axiosInstance.get(API.VERIFY_EMAIL_LINK, {
      params: { user_id, token },
    });
    return { success: true, data: response.data };
  } catch (e) {
    return axiosException(e);
  }
};

export const verifyEmailOtp = async (user_id, otp) => {
  try {
    const response = await axiosInstance.post(API.VERIFY_EMAIL, { user_id, otp });
    return { success: true, data: response.data };
  } catch (e) {
    return axiosException(e);
  }
};

/**
 * Resends the email OTP for a registered user
 * @param {number} user_id - the user ID returned from registration
 * @returns {Promise<object>} - object containing success, data and message
 */
export const resendEmailOtp = async (user_id) => {
  try {
    const response = await axiosInstance.post(API.RESEND_VERIFY_EMAIL, { user_id });
    return { success: true, data: response.data };
  } catch (e) {
    return axiosException(e);
  }
};

/**
 * Gets a list of reminders links from the API
 * @param {string} email - the email to get the reminders links for
 * @returns {Promise<string[]>} - list of reminders links
 * @throws {object} - error object containing message and status
 */
export const getRemindersLinks = async (email) => {
  try {
    const response = await axiosInstance.get(
      `${API.SLACK_LINK}?email=${email}`
    );
    return response.data.data;
  } catch (e) {
    return axiosException(e);
  }
};
